import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  FilePdf,
  FileDoc,
  Eye,
  DownloadSimple,
  Trash,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import api from "../lib/api";
import { LEVEL_META } from "../lib/risk";
import { useI18n } from "../lib/i18n";

export default function History() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["analyses"],
    queryFn: async () => (await api.get("/analyses")).data,
  });

  const download = async (a) => {
    setBusy(a.id + "-r");
    try {
      const res = await api.get(`/analyses/${a.id}/report`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `LEXADM_${(a.filename || "documento").replace(/\.[^.]+$/, "")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(lang === "pt" ? "Falha ao gerar o relatório." : "Failed to generate report.");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (a) => {
    setBusy(a.id + "-d");
    try {
      await api.delete(`/analyses/${a.id}`);
      qc.invalidateQueries({ queryKey: ["analyses"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success(lang === "pt" ? "Análise excluída." : "Analysis deleted.");
    } catch (e) {
      toast.error(lang === "pt" ? "Falha ao excluir." : "Failed to delete.");
    } finally {
      setBusy(null);
      setToDelete(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
      <header className="flex items-center justify-between animate-fade-up">
        <div>
          <p className="label-caps text-gold">LEXADM</p>
          <h1 className="font-display font-bold text-4xl tracking-tight text-navy mt-2">
            {t("hist.title")}
          </h1>
        </div>
        <button
          onClick={() => navigate("/")}
          data-testid="hist-new-btn"
          className="inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold rounded-md px-5 py-2.5 hover:bg-black transition-colors duration-200"
        >
          <MagnifyingGlass size={18} weight="bold" />
          {t("nav.new")}
        </button>
      </header>

      <div className="mt-8 bg-white border border-slate-200 rounded-lg overflow-hidden animate-fade-up" style={{ animationDelay: "60ms" }}>
        {isLoading ? (
          <div className="px-6 py-12 text-center text-sm text-slate-400">{t("common.loading")}</div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-slate-400" data-testid="hist-empty">
            {t("hist.empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="hist-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="label-caps px-5 py-3">{t("hist.document")}</th>
                  <th className="label-caps px-5 py-3 hidden sm:table-cell">{t("hist.date")}</th>
                  <th className="label-caps px-5 py-3">{t("hist.risk")}</th>
                  <th className="label-caps px-5 py-3 hidden sm:table-cell">{t("hist.score")}</th>
                  <th className="label-caps px-5 py-3 hidden md:table-cell">{t("hist.status")}</th>
                  <th className="label-caps px-5 py-3 text-right">{t("hist.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((a) => {
                  const meta = LEVEL_META[a.level] || LEVEL_META.MEDIO;
                  return (
                    <tr key={a.id} className="hover:bg-slate-50 transition-colors" data-testid={`hist-row-${a.id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <span className="text-slate-300 shrink-0">
                            {a.ext === "pdf" ? <FilePdf size={20} weight="fill" /> : <FileDoc size={20} weight="fill" />}
                          </span>
                          <span className="text-sm font-semibold text-navy truncate max-w-[180px]">{a.filename}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-500 hidden sm:table-cell">
                        {new Date(a.created_at).toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US")}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className="inline-block whitespace-nowrap px-2.5 py-1 rounded text-[11px] font-bold tracking-widest"
                          style={{ color: meta.color, background: meta.bg }}
                        >
                          {t(`risk.${a.level}`)}
                        </span>
                      </td>
                      <td className="px-5 py-4 hidden sm:table-cell">
                        <span className="font-display font-bold text-lg text-navy">{a.score}</span>
                      </td>
                      <td className="px-5 py-4 text-xs text-slate-500 hidden md:table-cell">{a.status}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/result/${a.id}`)}
                            data-testid={`hist-open-${a.id}`}
                            title={t("hist.open")}
                            className="p-2 rounded-md text-slate-500 hover:text-navy hover:bg-slate-100 transition-colors"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => download(a)}
                            disabled={busy === a.id + "-r"}
                            data-testid={`hist-report-${a.id}`}
                            title={t("hist.report")}
                            className="p-2 rounded-md text-slate-500 hover:text-gold hover:bg-slate-100 transition-colors disabled:opacity-50"
                          >
                            <DownloadSimple size={18} />
                          </button>
                          <button
                            onClick={() => setToDelete(a)}
                            disabled={busy === a.id + "-d"}
                            data-testid={`hist-delete-${a.id}`}
                            title={t("hist.delete")}
                            className="p-2 rounded-md text-slate-500 hover:text-risk-critical hover:bg-slate-100 transition-colors disabled:opacity-50"
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {lang === "pt" ? "Excluir análise?" : "Delete analysis?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {lang === "pt"
                ? `A análise de "${toDelete?.filename}" e seus achados serão removidos permanentemente.`
                : `The analysis of "${toDelete?.filename}" and its findings will be permanently removed.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">
              {lang === "pt" ? "Cancelar" : "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => toDelete && remove(toDelete)}
              data-testid="delete-confirm"
              className="bg-risk-critical hover:bg-risk-critical/90"
            >
              {t("hist.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
